import { useAtom } from '@reatom/npm-react'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, CloseIcon } from '@blade/ui-kit'
import { PlatformDropdown } from '@root/entities/platform/ui'
import { featureFlagsAtom } from '@root/shared/config/atoms/feature-flags'
import { SearchIcon } from '@root/shared/ui-kit'

import { searchQueryPlatformAtom } from '../atoms'

type Props = {
  query: string
  onQueryChange: (newQuery: string) => void
  onOpenChange: (open: boolean) => void
}

export const SearchModalQuery: React.FC<Props> = ({
  onQueryChange,
  query,
  onOpenChange,
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)

  const [platform, setPlatform] = useAtom(searchQueryPlatformAtom)
  const [featureFlags] = useAtom(featureFlagsAtom)

  const handleClear = () => onQueryChange('')

  return (
    <Root ref={ref}>
      <Search>
        <Icon size={18} $active={!!query} />
        <Input
          spellCheck={false}
          placeholder={t('currency.search_ticker_token_or_address')}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
        />
        <Clear
          variant="ghost"
          size="xxs"
          disabled={!query}
          onClick={handleClear}
        >
          {t('common.clear')}
        </Clear>
        {featureFlags.platformsEnabled ? (
          <PlatformDropdown
            container={ref}
            value={platform}
            onChange={setPlatform}
          />
        ) : undefined}
      </Search>
      <Close
        size="xs"
        variant="ghost"
        iconRight={<CloseIcon size={20} />}
        onClick={() => onOpenChange(false)}
      />
    </Root>
  )
}

const Root = styled.header`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto;

  gap: 8px;

  padding-inline: 15px;
  padding-block: 15px 0;
`

const Clear = styled(Button)`
  :disabled {
    color: var(--text-8);
  }
`

const Close = styled(Button)`
  color: var(--textA-8);

  cursor: pointer;

  &:hover {
    color: var(--text-12);
  }
`

const Search = styled.div`
  background: var(--appearance-1);

  display: grid;
  grid-template-columns: auto 1fr auto auto;

  align-items: center;
  padding-inline: 12px 4px;
  border-radius: 8px;

  height: 50px;

  gap: 10px;
`

const Input = styled.input`
  color: var(--text-12);

  background: transparent;
  border: none;
  outline: none;

  font-size: 14px;
  height: 100%;

  placeholder {
    color: var(--text-9);
  }
`

const Icon = styled(SearchIcon)<{ $active: boolean }>`
  color: ${({ $active }) => ($active ? 'var(--text-12)' : 'var(--text-9)')};
`
